import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  Spinner,
  Select,
  SimpleGrid,
  ModalOverlay,
  ModalContent,
  Icon,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Menu,
  MenuItem,
} from "@chakra-ui/react";
import { FaEdit, FaTint } from "react-icons/fa";
import {
  FaPhone,
  FaUser,
  FaAt,
  FaBirthdayCake,
  FaIdCard,
  FaFileImage,
  FaGenderless,
  FaBriefcaseMedical,
  FaUserAlt,
} from "react-icons/fa";
import { BASE_URL } from "../../Globalapi/Globalapi";
const EditTeacherModal = ({ teacherId, onTeacherUpdated }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [teacherData, setTeacherData] = useState({});
  const [bloodGroups, setBloodGroups] = useState([]);
  const [genders, setGenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Fetch Blood Groups
  const fetchBloodGroups = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/admin/staff/blood_group_master.php`
      );
      const data = await response.json();
      setBloodGroups(data.data || []);
    } catch (error) {
      console.error("Error fetching blood groups:", error);
      toast({
        title: "Error fetching blood groups.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Fetch Genders
  const fetchGenders = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/admin/staff/gender_master.php`
      );
      const data = await response.json();
      setGenders(data.data || []);
    } catch (error) {
      console.error("Error fetching genders:", error);
      toast({
        title: "Error fetching genders.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchTeacherDetails = async () => {
    try {
      setLoading(true);
      console.log(`Fetching details for teacher ID: ${teacherId}`);
      const response = await fetch(
        `${BASE_URL}/admin/staff/single_staff_list.php?user_id=${teacherId}`
      );
      const data = await response.json();
      console.log("Fetched teacher data:", data);
      setTeacherData(data.data || {});
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching teacher details:", error);
      toast({
        title: "Error fetching teacher details.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleEditClick = () => {
    console.log("Editing teacher with ID:", teacherId);
    onOpen();
    fetchTeacherDetails();
    fetchBloodGroups();
    fetchGenders();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTeacherData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Define the data to submit
      const dataToSubmit = {
        user_id: teacherData.user_id,
        name: teacherData.name,
        address: teacherData.address,
        email: teacherData.email,
        place: teacherData.place,
        gender: teacherData.gender_id,
        blood_group: teacherData.blood_group_id,
        contact_no: teacherData.contact_no,
        date_of_birth: teacherData.date_of_birth,
        date_of_join: teacherData.date_of_join,
        aadhar_no: teacherData.aadhar_no,
        guardian_name: teacherData.guardian_name,
        guardian_contact_no: teacherData.guardian_contact_no,
        guardian_address: teacherData.guardian_address,
        relation: teacherData.relation,
        thumb_img: teacherData.thumb_img, // Optional: Handle image upload
        // Include any other fields you need to submit
      };

      // Log the data being submitted
      console.log("Submitting teacher data:", dataToSubmit);

      const response = await fetch(
        `${BASE_URL}/admin/staff/update_staff.php`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(dataToSubmit),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Teacher updated.",
          description: "The teacher details have been updated successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onTeacherUpdated();
        onClose();
      } else {
        throw new Error(result.message || "Failed to update teacher.");
      }
    } catch (error) {
      toast({
        title: "Error updating teacher.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* <Button size="sm" colorScheme="blue" mr={2} leftIcon={<FaEdit />} onClick={handleEditClick}>
                Edit
            </Button> */}
      <Menu>
        <MenuItem
          p={0}
          icon={<FaEdit color="black" />}
          aria-label="Options"
          onClick={handleEditClick}
        >
          Edit
        </MenuItem>
      </Menu>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaEdit} boxSize={5} mr={2} />
            Edit Teacher Details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl id="name" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaUser}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Name
                </FormLabel>
                <Input
                  name="name"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.name || ""}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="date_of_birth" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaBirthdayCake}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Date of Birth
                </FormLabel>
                <Input
                  name="date_of_birth"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.date_of_birth || ""}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="email" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaAt}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Email
                </FormLabel>
                <Input
                  name="email"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.email || ""}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="address" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaBriefcaseMedical}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Address
                </FormLabel>
                <Input
                  name="address"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.address || ""}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="contact_no" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaPhone}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Contact No
                </FormLabel>
                <Input
                  name="contact_no"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.contact_no || ""}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="place" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaBriefcaseMedical}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Place
                </FormLabel>
                <Input
                  name="place"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.place || ""}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="blood_group_id" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaBriefcaseMedical}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Blood Group
                </FormLabel>
                <Select
                  name="blood_group_id"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.blood_group_id || ""}
                  onChange={handleInputChange}
                >
                  {bloodGroups.length > 0 &&
                    bloodGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.group}
                      </option>
                    ))}
                </Select>
              </FormControl>

              <FormControl id="gender_id" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaUserAlt}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Gender
                </FormLabel>
                <Select
                  name="gender_id"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.gender_id || ""}
                  onChange={handleInputChange}
                >
                  {genders.length > 0 &&
                    genders.map((gender) => (
                      <option key={gender.id} value={gender.id}>
                        {gender.gender}
                      </option>
                    ))}
                </Select>
              </FormControl>

              <FormControl id="aadhar_no" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaIdCard}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Aadhar No
                </FormLabel>
                <Input
                  name="aadhar_no"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.aadhar_no || ""}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="guardian_name" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaUser}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Guardian Name
                </FormLabel>
                <Input
                  name="guardian_name"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.guardian_name || ""}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="relation" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaTint}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Relation
                </FormLabel>
                <Input
                  name="relation"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.relation || ""}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="guardian_contact_no" isRequired>
                <FormLabel>
                  {" "}
                  <Icon
                    as={FaPhone}
                    color="teal.500"
                    style={{ marginRight: "8px" }}
                  />
                  Guardian Contact No
                </FormLabel>
                <Input
                  name="guardian_contact_no"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.guardian_contact_no || ""}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl id="guardian_address" isRequired>
                <FormLabel>Guardian Address</FormLabel>
                <Input
                  name="guardian_address"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={teacherData.guardian_address || ""}
                  onChange={handleInputChange}
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>

          <ModalFooter>
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              mr={3}
              onClick={handleSubmit}
              isLoading={loading}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditTeacherModal;
